import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { OpenGelderlandInformationFieldsFragment, OpenGelderlandWooOverview } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetOpenWooRequestOverview } from 'api/graphql-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState } from 'redux/stores/woo-requests';
import { IBasePageProps } from 'types/page';
import { OPEN_GELDERLAND_ROUTES } from 'constants/routes';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import Layout from 'modules/layout/layout';
import WooDocumentOverview, { WOO_REQUEST_OVERVIEW_LIMIT } from 'modules/woo-document-overview/woo-document-overview';

interface IOpenGelderlandOverviewPageProps extends IBasePageProps {
	openGelderlandWooOverview?: OpenGelderlandWooOverview;
	openGelderlandInformation?: OpenGelderlandInformationFieldsFragment;
	themes: any[];
}

const OpenGelderlandWooDocumentOverviewPage = (props: IOpenGelderlandOverviewPageProps): React.JSX.Element => {
	const router = useRouter();
	const modules = props.openGelderlandWooOverview?.modules;
	const seoModule = props?.openGelderlandWooOverview?.seo;
	const wooOverviewModule = props?.openGelderlandWooOverview?.woo_overview;

	const onSearch = async (search: string): Promise<void> => {
		router.push({ pathname: '/woo-documenten', query: { zoekterm: search } });
	};

	return (
		<Layout
			navbar={props?.openGelderlandInformation?.navbar}
			footer={props?.openGelderlandInformation?.footer}
			customSearch={onSearch}
		>
			<Head seoModule={seoModule} seoParent={props?.openGelderlandInformation?.seo} />
			<IntroHeader
				showBreadCrumbs={true}
				module={props?.openGelderlandWooOverview?.header as IModule}
				slug={OPEN_GELDERLAND_ROUTES.WOO_DOCUMENTS.fullPath}
			/>
			{wooOverviewModule && <WooDocumentOverview module={wooOverviewModule} />}
			<ModuleBuilder modules={modules} slug={OPEN_GELDERLAND_ROUTES.WOO_DOCUMENTS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IBasePageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const openGelderlandWooDocumentsOverviewPageResult = await getServerPageGetOpenWooRequestOverview(
			{
				variables: {
					locale,
					publicationState,
					limit: WOO_REQUEST_OVERVIEW_LIMIT
				}
			},
			client
		);

		const {
			openGelderlandWooOverview,
			openGelderlandInformation,
			wooCategories,
			wooRequests_connection: wooRequests,
			countDocumentsPerCategory
		} = openGelderlandWooDocumentsOverviewPageResult.props.data;

		if (openGelderlandWooOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				openGelderlandWooOverview: openGelderlandWooOverview.data?.attributes,
				openGelderlandInformation: openGelderlandInformation?.data?.attributes,
				initialReduxState: {
					wooRequests: {
						...initialState,
						wooRequests: wooRequests?.data?.map((request) => request.attributes) ?? [],
						wooRequestsCount: wooRequests?.pageInfo.total || 0,
						wooRequestsCountPerCategory: countDocumentsPerCategory,
						categories: wooCategories?.map((category) => ({
							id: category.data.documentId,
							label: category.data.attributes.title,
							apiLabel: category.data.attributes?.sitemap
						})) as any[]
					},
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'woo-documents-overview',
						contentTypeId: openGelderlandWooOverview?.data?.documentId,
						showNeutralStyle: false,
						hideLanguageSwitcher: true,
						hideSearchbar: false,
						isSubsite: true
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('error in Open Gelderland Woo Documents Page', error);
		return {
			notFound: true
		};
	}
};

export default OpenGelderlandWooDocumentOverviewPage;
